/**
 * Client-side sorting for queue pages that page their own rows.
 *
 * b-table sorts only the slice handed to it, so a page that slices before rendering would sort
 * within the current page and leave the ordering wrong across page boundaries. Queue pages
 * therefore sort the full set first and slice second, which is what this exists to do.
 */

/**
 * Orders rows by one property.
 *
 * Blank cells sort last in *both* directions rather than flipping to the top when reversed. A row
 * missing the value being sorted on is not the largest such value — it is a row with nothing to
 * compare — and floating those to the top of a descending sort buries the rows that do have one.
 *
 * The sort is stable, so rows the comparator calls equal keep the order the API returned them in.
 */
export function sortRows<T>(rows: T[], sortBy: string | null, sortDesc: boolean): T[] {
    const sorted = [...rows];
    if (!sortBy) return sorted;

    sorted.sort((a, b) => {
        const aValue = (a as any)[sortBy];
        const bValue = (b as any)[sortBy];

        if (isBlank(aValue) && isBlank(bValue)) return 0;
        if (isBlank(aValue)) return 1;
        if (isBlank(bValue)) return -1;

        const result = compareValues(aValue, bValue);
        return sortDesc ? -result : result;
    });

    return sorted;
}

/** The rows visible on one page, given a 1-based page number. */
export function pageRows<T>(rows: T[], pageNumber: number, pageSize: number): T[] {
    const startIndex = pageSize * pageNumber - pageSize;
    return rows.slice(startIndex, startIndex + pageSize);
}

function isBlank(value: any): boolean {
    return value === null || value === undefined || value === "";
}

/**
 * Compares two cell values without being told their type. Queue columns mix numbers, ISO date
 * strings and free text, and typing each column at every call site is how a column silently ends
 * up sorted as text — which puts 10 before 9 and is only noticed once a queue is long enough.
 */
function compareValues(a: any, b: any): number {
    if (typeof a === "number" && typeof b === "number") return a - b;
    if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);

    const aDate = asDate(a);
    const bDate = asDate(b);
    if (aDate !== null && bDate !== null) return aDate - bDate;

    return String(a).localeCompare(String(b), "en-US", { numeric: true, sensitivity: "base" });
}

/**
 * Recognises the ISO-8601 timestamps the API returns, and nothing else. Deliberately not
 * Date.parse against any string: that accepts values like "Paid" in some engines, which would turn
 * a status column into a date sort.
 */
function asDate(value: any): number | null {
    if (value instanceof Date) return value.getTime();
    if (typeof value !== "string") return null;
    if (!/^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/.test(value)) return null;

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
}
